export default function MonroeHelpfulResourcesSection() {
  const photoLocations = [
    { name: "CVS Pharmacy — Sterlington Rd", address: "4901 Sterlington Rd, Monroe, LA 71203", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Louisville Ave", address: "1421 Louisville Ave, Monroe, LA 71201", hours: "Call for hours" },
    { name: "CVS Pharmacy — Thomas Rd", address: "2500 Thomas Rd, West Monroe, LA 71291", hours: "Daily 7 AM – 11 PM" },
    { name: "The UPS Store — Monroe", address: "1601 N 18th St, Monroe, LA 71201", hours: "Call for hours" },
    { name: "Walgreens — West Monroe", address: "100 Forsythe Ave, West Monroe, LA 71291", hours: "Call for hours" },
    { name: "FedEx Office — Monroe", address: "4900 Frontage Rd, Monroe, LA 71203", hours: "Call for hours" },
  ];

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Monroe Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local Northeast Louisiana resources to help you gather everything you need.
          </p>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near Monroe</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                Your passport application requires two identical 2&quot;&times;2&quot; passport photos taken within the last 6 months. Popular passport photo locations near Monroe and West Monroe include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {photoLocations.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          {/* Vital Records */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Louisiana Birth, Death, Marriage &amp; Divorce Records</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                If you need a certified copy of your birth certificate for your passport application — or need to update other vital records — the Louisiana Department of Health Vital Records Registry handles all statewide vital records requests. Ouachita Parish-specific records can also be obtained through the Ouachita Parish Clerk of Court.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  {
                    title: "Louisiana Vital Records Registry",
                    description: "Order certified copies of Louisiana birth and death certificates online, by mail, or in person at the state office in New Orleans.",
                    url: "https://ldh.la.gov/page/vital-records",
                    label: "LA Vital Records Registry",
                    featured: true,
                  },
                  {
                    title: "VitalChek \u2014 Louisiana Birth &amp; Death Certificates",
                    description: "Third-party expedited service for Louisiana birth and death certificates. Faster processing for an additional fee.",
                    url: "https://www.vitalchek.com/",
                    label: "Order Online via VitalChek",
                    featured: false,
                  },
                ].map((resource, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${resource.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{resource.title}</p>
                    <p className="text-gray-500 text-xs leading-relaxed mb-3">{resource.description}</p>
                    <a href={resource.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{resource.label}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-information-line text-amber-500 mr-1.5"></i>
                  <strong>Marriage and divorce records</strong> are maintained by the Ouachita Parish Clerk of Court, not the state vital records office. Contact the parish clerk directly for certified copies of marriage licenses and divorce decrees issued in Ouachita Parish.
                </p>
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3 mt-4">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-[#F6C344] mr-1.5"></i>
                  <strong>Passport tip:</strong> Your birth certificate must be a certified copy with a raised seal, stamp, or registrar&apos;s signature. Photocopies and hospital-issued certificates are not accepted by the U.S. Department of State.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Monroe Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {[
                  { code: "MLU", name: "Monroe Regional Airport", distance: "3 miles northwest of downtown", address: "5400 Operations Rd, Monroe, LA 71203", note: "Primary local airport. Connections to Dallas-Fort Worth and Atlanta for international routes. Convenient for Monroe and West Monroe travelers.", featured: true },
                  { code: "SHV", name: "Shreveport Regional Airport", distance: "~90 miles northwest of Monroe", address: "5103 Hollywood Ave, Shreveport, LA 71109", note: "Secondary option with additional routes. Some Monroe travelers use SHV for better connection options to international hubs.", featured: false },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Local</span>}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs mb-1">{airport.name}</p>
                    <p className="text-[#F6C344] text-xs font-semibold mb-1">{airport.distance}</p>
                    <p className="text-gray-500 text-xs mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed">{airport.note}</p>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing will not make it — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}